<template>
	<div ref="el" :class="{ 'v-dropdown': true, 'v-dropdown-bubble': variant === `bubble` }">
		<button
			:id="trigger_id"
			ref="trigger_el"
			type="button"
			:class="trigger_classes"
			aria-haspopup="true"
			:aria-expanded="is_open"
			:aria-controls="panel_id"
			@click="toggle"
		>
			<slot name="trigger" :is_open="is_open">
				<v-icon v-if="icon" :slug="icon" />

				<span v-if="mobile_label && !has_value" class="label-mobile">{{ mobile_label }}</span>

				<span class="label-full">{{ label }}</span>

				<v-icon slug="angle-down" />
			</slot>
		</button>

		<teleport to="body" :disabled="!is_affixed">
			<div v-if="is_open && is_affixed" class="dropdown-backdrop" @click="close" />

			<div
				v-if="is_open"
				:id="panel_id"
				ref="panel_el"
				:class="{ dropdown: true, 'dropdown-affixed': is_affixed }"
				role="group"
				:aria-labelledby="trigger_id"
			>
				<span v-if="is_affixed" class="dropdown-panel-label">{{ full_label || label }}</span>

				<slot :close="close" />
			</div>
		</teleport>
	</div>
</template>

<style lang="scss" scoped>
@use 'sass:math';

@use '/sass/variables' as *;
@use '/sass/toolkit' as *;

.v-dropdown {
	position: relative;

	&:not(.v-dropdown-bubble) {
		max-width: rfs(8, 10);
		width: 100%;
	}
}

.dropdown-trigger {
	display: flex;
	align-items: center;
	width: 100%;
	min-height: $control-height;
	gap: 0.5em;
	padding: 0 calc(1em - 2px);
	border: 1px solid $grey-border;
	border-radius: 9999px;
	background-color: $white;
	white-space: nowrap;

	&.is-open {
		// border-color: $orange;
		// color: $black;

		svg {
			transform: scaleY(-1);
		}
	}

	&.has-value {
		background-color: $orange;
		color: $white;
		border-color: transparent;
		font-weight: bold;

		svg {
			fill: $white;
		}
	}

	span {
		text-overflow: ellipsis;
		overflow: hidden;
		font-size: 0.875rem;
	}

	.label-mobile {
		display: none;
	}

	@include media('<=phone') {
		span {
			font-size: 0.875em;
		}

		.label-mobile {
			display: inline;
		}

		.label-mobile ~ .label-full {
			display: none;
		}
	}

	svg {
		display: block;
		flex: 0 0 auto;
		width: 1em;
		height: 1em;
		margin-left: auto;
		fill: $grey-meta;
	}
}

.v-dropdown-bubble {
	min-width: 0;
	max-width: none;
}

.bubble-trigger {
	cursor: pointer;

	&.is-open svg:last-child {
		transform: scaleY(-1);
	}

	svg {
		display: block;
		flex: 0 0 auto;
		width: 1em;
		height: 1em;

		&:last-child {
			fill: $grey-meta;
		}
	}
}

.dropdown {
	position: absolute;
	top: 100%;
	left: 0;
	overflow: hidden;
	min-width: 100%;
	width: max-content;
	max-width: 20em;
	padding-top: 0.5em;
	padding-bottom: 0.5em;
	border-radius: 1em;
	border: 1px solid $grey-border;
	z-index: 10;
	background-color: $white;
	margin-top: math.div($spacer, 2);
}

.dropdown-affixed {
	position: fixed;
	top: auto;
	left: 0;
	right: 0;
	bottom: 0;
	overflow-y: auto;
	width: auto;
	max-width: none;
	max-height: 80vh;
	padding-top: 1em;
	padding-bottom: 1em;
	border-radius: 1em 1em 0 0;
	border-bottom: none;
	margin-top: 0;
	z-index: 1000;
}

.dropdown-panel-label {
	display: block;
	margin: 0 1em 0.5em;
	padding-bottom: 0.5em;
	border-bottom: 1px solid $grey-border;
	font-weight: bold;
	color: $black;
}

.dropdown-backdrop {
	position: fixed;
	inset: 0;
	background-color: rgba($black, 0.4);
	z-index: 999;
}
</style>

<script setup>
import { onClickOutside, onKeyStroke, useMediaQuery } from '@vueuse/core';
import { computed, nextTick, ref, useId } from 'vue';

import VIcon from './_v-icon.vue';

const props = defineProps({
	label: {
		type: String,
		required: false,
		default: ``,
	},
	full_label: {
		type: String,
		required: false,
		default: null,
	},
	mobile_label: {
		type: String,
		required: false,
		default: null,
	},
	has_value: {
		type: Boolean,
		required: false,
	},
	variant: {
		type: String,
		required: false,
		default: `pill`,
	},
	color: {
		type: String,
		required: false,
		default: `blue`,
	},
	icon: {
		type: String,
		required: false,
		default: null,
	},
});

const el = ref(null);

const trigger_el = ref(null);

const panel_el = ref(null);

const trigger_id = useId();

const panel_id = useId();

const is_affixed = useMediaQuery(`(max-width: 1080px)`);

const is_open = defineModel(`open`, {
	type: Boolean,
	default: false,
});

const trigger_classes = computed(() => {
	if (props.variant === `bubble`) {
		return [
			`btn`,
			`bubble`,
			props.color,
			`small`,
			`bubble-trigger`,
			{
				'is-open': is_open.value,
			},
		];
	}

	return [
		`btn`,
		`dropdown-trigger`,
		{
			'is-open': is_open.value,
			'has-value': props.has_value,
		},
	];
});

function toggle() {
	if (is_open.value) {
		close();
		return;
	}

	open();
}

function open() {
	is_open.value = true;

	nextTick(() => {
		const focusable = panel_el.value?.querySelector(`input, button, [tabindex]`);

		focusable?.focus();
	});
}

function close() {
	if (!is_open.value) {
		return;
	}

	is_open.value = false;

	trigger_el.value?.focus();
}

onClickOutside(el, close, {
	ignore: [panel_el],
});

onKeyStroke(`Escape`, close);
</script>
